import { createContext, useCallback, useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';

import {
  convertValueToMillimeters,
  DIMENSION_TYPES,
  PART_FILE_UNITS,
  PREFERRED_UNIT_SYSTEMS,
  RUNES_CALLOUT_TYPES,
  Value,
} from '@frontend/api';
import { noop } from '@frontend/utils';

import type { AnnotationActionCallbackWithLoading } from 'modules/common/components/findings';
import { getActiveFile } from 'modules/common/viewer/data/reducer';
import type { ISuggestionAnnotation } from 'modules/common/viewer/pdfViewer/annotationCommands/SuggestionAnnotation';
import { SUGGESTION_ANNOTATION_TYPES } from 'modules/common/viewer/pdfViewer/annotationCommands/consts';
import {
  ANNOTATION_METADATA_TYPES,
  type FoundInFileMetadata,
} from 'modules/common/viewer/pdfViewer/annotationCommands/types';
import { useUpdatePartDimensions } from 'modules/viewerPage/apps/quotePartViewerApps/partSetup/components/DimensionFields';
import { getActivePart } from 'modules/viewerPage/data/reducer';

import { getPreferredUnitSystem } from 'store/session/selectors';

export type GeometricAnnotationActions = {
  setSizeX: AnnotationActionCallbackWithLoading;
  setSizeY: AnnotationActionCallbackWithLoading;
  setSizeZ: AnnotationActionCallbackWithLoading;
  getEqualDimensions: () => string[];
};

type GeometricActionData = {
  geometricActions: ({
    partBotResultUuid,
    value,
    sizeExtractionUuids,
  }: {
    partBotResultUuid: string | null;
    value: Value;
    sizeExtractionUuids: SizeExtractionUuids;
  }) => GeometricAnnotationActions;
  processAnnotation: (annotation: ISuggestionAnnotation | null) => {
    partBotResultUuid: string | null;
    value: Value;
    sizeExtractionUuids: SizeExtractionUuids;
  } | null;
};

export const DEFAULT_GEOMETRIC_ACTIONS = () => ({
  setSizeX: {
    fn: noop,
    isLoading: false,
  },
  setSizeY: {
    fn: noop,
    isLoading: false,
  },
  setSizeZ: {
    fn: noop,
    isLoading: false,
  },
  getEqualDimensions: () => [],
});

const DEFAULT_ACTION_DATA: GeometricActionData = {
  geometricActions: DEFAULT_GEOMETRIC_ACTIONS,
  processAnnotation: () => null,
};

const GeometricActionDataContext =
  createContext<GeometricActionData>(DEFAULT_ACTION_DATA);

type GeometricActionDataProviderProps = {
  children: React.ReactNode;
  canEditPart: boolean;
};

type SizeExtractionUuids = {
  x?: string | null;
  y?: string | null;
  z?: string | null;
};

export const GeometricActionDataProvider = ({
  children,
  canEditPart,
}: GeometricActionDataProviderProps) => {
  const activeFile = useSelector(getActiveFile);
  const { printUnits } = activeFile ?? {};
  const activePart = useSelector(getActivePart);
  const { partFiles, primaryFileUuid } = activePart ?? {};

  const primaryPartFile = Array.isArray(partFiles)
    ? partFiles.find((partFile) => partFile.uuid === primaryFileUuid)
    : undefined;

  const hasGeometry = primaryPartFile?.hasGeometry;

  const preferredUnitSystem = useSelector(getPreferredUnitSystem);
  const fallbackUnits =
    preferredUnitSystem === PREFERRED_UNIT_SYSTEMS.IMPERIAL
      ? PART_FILE_UNITS.IN
      : PART_FILE_UNITS.MM;

  const { updatePartForBaq, isUpdatingPartDimensions } =
    useUpdatePartDimensions();

  const canUseGeometricActions = useMemo(
    () => !canEditPart || !activePart || !activeFile || hasGeometry,
    [canEditPart, activePart, activeFile, hasGeometry],
  );

  const processAnnotation = useCallback(
    (
      annot: ISuggestionAnnotation | null,
    ): {
      partBotResultUuid: string | null;
      value: Value;
      sizeExtractionUuids: SizeExtractionUuids;
    } | null => {
      if (
        canUseGeometricActions ||
        !annot ||
        annot.rawAnnotation.type !==
          SUGGESTION_ANNOTATION_TYPES.FOUND_IN_FILES ||
        !annot.rawAnnotation.metadata ||
        annot.rawAnnotation.metadata.type !==
          ANNOTATION_METADATA_TYPES.FOUND_IN_FILES
      ) {
        return null;
      }

      const metadata = annot.rawAnnotation.metadata as FoundInFileMetadata;
      const {
        partBotResult: { rawCallout, uuid: partBotResultUuid },
      } = metadata;

      if (
        !rawCallout ||
        rawCallout.callout.calloutType !== RUNES_CALLOUT_TYPES.DIM ||
        rawCallout.callout.dimType === DIMENSION_TYPES.ANGULAR
      ) {
        return null;
      }
      const {
        callout: {
          meas: { value },
        },
      } = rawCallout;

      return {
        partBotResultUuid,
        value,
        sizeExtractionUuids: {
          x: activePart?.sizeXExtractionUuid,
          y: activePart?.sizeYExtractionUuid,
          z: activePart?.sizeZExtractionUuid,
        },
      };
    },
    [canUseGeometricActions, activePart],
  );

  const geometricActions = useCallback(
    ({
      partBotResultUuid,
      value,
      sizeExtractionUuids,
    }: {
      partBotResultUuid: string | null;
      value: Value;
      sizeExtractionUuids: SizeExtractionUuids;
    }): GeometricAnnotationActions => {
      const valueInMillimeters = convertValueToMillimeters(
        value,
        printUnits ?? fallbackUnits,
      );

      return {
        setSizeX: {
          fn: () => {
            return updatePartForBaq({
              sizeX: valueInMillimeters,
              sizeXExtractionUuid: partBotResultUuid,
              units: 'mm',
            });
          },
          isLoading: isUpdatingPartDimensions,
        },
        setSizeY: {
          fn: () => {
            return updatePartForBaq({
              sizeY: valueInMillimeters,
              sizeYExtractionUuid: partBotResultUuid,
              units: 'mm',
            });
          },
          isLoading: isUpdatingPartDimensions,
        },
        setSizeZ: {
          fn: () => {
            return updatePartForBaq({
              sizeZ: valueInMillimeters,
              sizeZExtractionUuid: partBotResultUuid,
              units: 'mm',
            });
          },
          isLoading: isUpdatingPartDimensions,
        },
        getEqualDimensions: () => {
          const out: string[] = [];
          if (sizeExtractionUuids.x === partBotResultUuid) {
            out.push('x');
          }
          if (sizeExtractionUuids.y === partBotResultUuid) {
            out.push('y');
          }
          if (sizeExtractionUuids.z === partBotResultUuid) {
            out.push('z');
          }
          return out;
        },
      };
    },
    [printUnits, fallbackUnits, updatePartForBaq, isUpdatingPartDimensions],
  );
  const value = useMemo<GeometricActionData>(
    () => ({
      processAnnotation,
      geometricActions,
    }),
    [processAnnotation, geometricActions],
  );

  return (
    <GeometricActionDataContext.Provider value={value}>
      {children}
    </GeometricActionDataContext.Provider>
  );
};

export const useGeometricActionData = (): GeometricActionData =>
  useContext(GeometricActionDataContext);
